import type { ITuningClock, IGuestTuningState, ResolvedTuningConfiguration, TuningCommand, TuningIdentity,
  TuningReceipt, TuningSessionGrant, TuningTrialControlContext, TuningTrialSnapshot, TuningPrepare } from '@playcastdotio/PeerCore';
import { canonicalTuningConfigurationJson, isTuningSettingValue } from '@playcastdotio/PeerCore/tuning';

/** Lease duration is accepted in this endpoint's clock, never subtracted across devices. */
export class GuestTrialState implements IGuestTuningState {
  private grant?: TuningSessionGrant;
  private expiresAtMs = 0;
  private current?: TuningTrialSnapshot;
  private context?: TuningTrialControlContext;
  private cleanup?: { context: TuningTrialControlContext; expiresAtMs: number };
  private cleanupStopId?: string;
  constructor(private readonly clock: ITuningClock, private readonly localPeerId: () => string) {}
  accept(grant: TuningSessionGrant): boolean {
    if (grant.target.peerId !== this.localPeerId()) return false;
    if (this.current && grant.target.sessionId === this.current.target.sessionId
      && grant.target.connectionEpoch < this.current.target.connectionEpoch) return false;
    this.grant = grant; this.expiresAtMs = this.clock.monotonicMs() + grant.leaseTtlMs;
    if (!this.current || !this.same(this.current.target, grant.target) || this.current.phase === 'stopped') this.reset(grant.target, grant.production);
    return true;
  }
  reset(target: TuningIdentity, configuration: ResolvedTuningConfiguration): void {
    this.current = { target: { ...target }, phase: 'connecting', requested: { ...configuration.settings },
      effective: { ...configuration.settings }, configurationHash: configuration.configurationHash,
      strategyId: configuration.strategyId, workloadId: 'interactive', receipts: [], latest: [] };
  }
  snapshot(): TuningTrialSnapshot { if (!this.current) throw new Error('Guest tuning session is not granted'); return this.current; }
  leaseId(): string | undefined { return this.grant?.leaseId; }
  production(): ResolvedTuningConfiguration | undefined { return this.grant?.production; }
  armed(): boolean { return !!this.grant && this.current?.phase !== 'stopped' && this.clock.monotonicMs() < this.expiresAtMs; }
  stop(): void { if (this.current) this.current = { ...this.current, phase: 'stopped' }; }
  validate(command: TuningCommand): { valid: true } | { valid: false; code: string; message: string } {
    const cleanupStop = command.action.kind === 'stop' && command.leaseId === this.grant?.leaseId
      && !!this.current && this.same(command.target, this.current.target);
    if ((!this.armed() && !cleanupStop) || command.leaseId !== this.grant?.leaseId) return { valid: false, code: 'expired-lease', message: 'Current guest lease is required' };
    if (!this.current || !this.same(command.target, this.current.target)) return { valid: false, code: 'stale-command', message: 'Trial generation changed' };
    if (cleanupStop) this.cleanupStopId = command.commandId;
    return { valid: true };
  }
  acceptsCleanupReceipt(receipt: TuningReceipt): boolean {
    return receipt.commandId === this.cleanupStopId && !!this.current && this.same(receipt.target, this.current.target);
  }
  acceptsAuthoritativeReceipt(receipt: TuningReceipt): boolean {
    const current = this.current?.target, incoming = receipt.target;
    return !!current && current.sessionId === incoming.sessionId && current.peerId === incoming.peerId
      && current.trialId === incoming.trialId && current.connectionEpoch === incoming.connectionEpoch
      && incoming.configRevision >= current.configRevision && incoming.configRevision <= current.configRevision + 1;
  }
  authorize(context: TuningTrialControlContext): boolean {
    const current = this.current;
    const cleanup = this.cleanup;
    const allowedCleanup = !!cleanup && this.clock.monotonicMs() < cleanup.expiresAtMs
      && context.commandId === cleanup.context.commandId && context.configurationHash === cleanup.context.configurationHash
      && context.target.trialId === cleanup.context.target.trialId && context.target.connectionEpoch === cleanup.context.target.connectionEpoch;
    return (this.armed() || allowedCleanup) && context.leaseId === this.grant?.leaseId && !!current
      && context.target.sessionId === current.target.sessionId && context.target.peerId === this.localPeerId()
      && context.target.connectionEpoch >= current.target.connectionEpoch;
  }
  authorizePreparation(prepare: TuningPrepare): boolean {
    if (this.authorize(prepare)) return true;
    const current = this.current, production = this.grant?.production;
    if (!current || !production || prepare.leaseId !== this.grant?.leaseId || prepare.target.sessionId !== current.target.sessionId
      || prepare.target.peerId !== this.localPeerId() || prepare.target.connectionEpoch <= current.target.connectionEpoch
      || prepare.configurationHash !== production.configurationHash
      || canonicalTuningConfigurationJson(prepare.resolvedConfiguration) !== canonicalTuningConfigurationJson(production)) return false;
    this.cleanup = { context: prepare, expiresAtMs: this.clock.monotonicMs() + 15_000 }; return true;
  }
  prepared(context: TuningTrialControlContext): void { this.context = context; }
  trialContext(): TuningTrialControlContext | undefined { return this.context; }
  commit(receipt: TuningReceipt, authoritative = false): void {
    if (!this.current) return;
    const target = this.current.target, incoming = receipt.target;
    if (target.sessionId !== incoming.sessionId || target.peerId !== incoming.peerId || target.trialId !== incoming.trialId
      || target.connectionEpoch !== incoming.connectionEpoch || incoming.configRevision < target.configRevision
      || incoming.configRevision > target.configRevision + (authoritative ? 1 : 0)) return;
    const effective = { ...this.current.effective };
    for (const setting of receipt.settings) if (['applied', 'clamped'].includes(setting.status)) {
      const value = [setting.readback, setting.negotiated, setting.accepted, setting.requested]
        .find(candidate => isTuningSettingValue(setting.key, candidate));
      if (value !== undefined) effective[setting.key] = value;
    }
    this.current = { ...this.current, effective, receipts: [...this.current.receipts, receipt].slice(-64),
      target: authoritative && receipt.stage === 'applied' ? { ...incoming } : { ...target } };
  }
  close(): void { this.grant = undefined; this.context = undefined; this.current = undefined; this.cleanup = undefined; this.cleanupStopId = undefined; this.expiresAtMs = 0; }
  private same(a: TuningIdentity, b: TuningIdentity): boolean {
    return a.sessionId === b.sessionId && a.peerId === b.peerId && a.trialId === b.trialId
      && a.connectionEpoch === b.connectionEpoch && a.configRevision === b.configRevision;
  }
}
